import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Typo } from '@/components/ui/typography';
import { UserCartItem, useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { router } from '@inertiajs/react';
import { Check, Loader2, Tag, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface VoucherInputProps {
  onVoucherApplied?: () => void;
  onVoucherRemoved?: () => void;
  selectedItems?: UserCartItem[];
}

export function VoucherInput({ onVoucherApplied, onVoucherRemoved, selectedItems }: VoucherInputProps) {
  const { applyVoucher, removeVoucher, appliedVoucher, isFetching } = useUserGlobalCart();
  const [voucherCode, setVoucherCode] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) {
      toast.error('Vui lòng nhập mã voucher');
      return;
    }

    setIsApplying(true);
    try {
      const response = await applyVoucher({ voucher_code: voucherCode.trim(), row_ids: selectedItems?.map((item) => item.rowId) || [] });

      if (response?.success) {
        const matchedCount = response.data?.cart?.matched_row_ids?.length || 0;
        const totalItems = response.data?.cart?.count || 0;
        const matchInfo = matchedCount > 0 ? ` (${matchedCount}/${totalItems} sản phẩm)` : '';
        toast.success((response.message || 'Áp dụng voucher thành công') + matchInfo);
        setVoucherCode('');
        // Refresh the shared `voucher` prop the order summary renders from,
        // otherwise the applied card and the discount line stay stale.
        router.reload({ only: ['appliedVoucher'] });
        onVoucherApplied?.();
      } else {
        toast.error(response?.message || 'Không thể áp dụng voucher');
      }
    } catch (error: any) {
      console.error('Error applying voucher:', error);
      toast.error(error?.message || 'Đã có lỗi xảy ra');
    } finally {
      setIsApplying(false);
    }
  };

  const handleRemoveVoucher = async () => {
    setIsApplying(true);
    try {
      const response = await removeVoucher();
      if (response?.success) {
        // toast.success(response.message || 'Đã xóa voucher');
        onVoucherRemoved?.();
        router.reload({ only: ['appliedVoucher'] });
      } else {
        toast.error(response?.message || 'Không thể xóa voucher');
      }
    } catch (error: any) {
      console.error('Error removing voucher:', error);
      toast.error(error?.message || 'Đã có lỗi xảy ra');
    } finally {
      setIsApplying(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !appliedVoucher) {
      e.preventDefault();
      handleApplyVoucher();
    }
  };

  return (
    <div className="space-y-3">
      {appliedVoucher ? (
        // Applied Voucher Display
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <Tag className="mt-0.5 h-4 w-4 text-green-600" />
              <div className="flex-1">
                <Typo variant="small" className="font-medium text-green-900">
                  Voucher đã áp dụng
                </Typo>
                <code className="mt-1 block font-mono text-sm font-bold text-green-700">{appliedVoucher.code}</code>
                {appliedVoucher.discount_label && (
                  <Typo variant="muted" className="mt-1 text-xs text-green-600">
                    Giảm {appliedVoucher.discount_label}
                  </Typo>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveVoucher}
              disabled={isApplying || isFetching}
              className="h-6 w-6 p-0 text-green-600 hover:bg-green-100 hover:text-green-700"
            >
              {isApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
            </Button>
          </div>
        </div>
      ) : (
        // Voucher Input
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-gray-500" />
            <Typo variant="small" className="font-medium text-gray-700">
              Ưu đãi
            </Typo>
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Nhập mã ưu đãi"
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              disabled={isApplying || isFetching}
              className="flex-1 font-mono text-sm"
            />
            <Button onClick={handleApplyVoucher} disabled={isApplying || isFetching || !voucherCode.trim()} size="sm" className="min-w-[80px]">
              {isApplying ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <>
                  <Check className="size-4" />
                  Áp dụng
                </>
              )}
            </Button>
          </div>
          <Typo variant="muted" className="text-xs">
            Nhập mã voucher để nhận ưu đãi
          </Typo>
        </div>
      )}
    </div>
  );
}
